/**
 * Custom Currency Converter for Multi-Market Support
 * Handles currency conversion based on locale from URL
 * Supports: Bahrain (BHD), KSA (SAR), UAE (AED), Kuwait (KWD), Oman (OMR), Qatar (QAR)
 * 
 * Features:
 * - Automatic locale detection from URL pattern (.com/en-bh, .com/ar-sa, etc.)
 * - Real-time price conversion with accurate exchange rates
 * - Proper currency formatting (right-to-left for Arabic)
 * - DOM mutation observer for dynamic content
 * - LocalStorage caching for performance
 * - Global API for external use
 */

class CurrencyConverter {
  constructor() {
    // Exchange rates relative to BHD (base currency)
    // Update these rates periodically based on live exchange rates
    this.currencies = {
      'en-bh': { code: 'BHD', symbol: 'BHD', country: 'Bahrain', countryAr: 'البحرين', rate: 1.0, direction: 'ltr' },
      'ar-bh': { code: 'BHD', symbol: '.د.ب', country: 'Bahrain', countryAr: 'البحرين', rate: 1.0, direction: 'rtl' },
      'en-sa': { code: 'SAR', symbol: 'SAR', country: 'Saudi Arabia', countryAr: 'المملكة العربية السعودية', rate: 9.95, direction: 'ltr' },
      'ar-sa': { code: 'SAR', symbol: 'ر.س', country: 'Saudi Arabia', countryAr: 'المملكة العربية السعودية', rate: 9.95, direction: 'rtl' },
      'en-ae': { code: 'AED', symbol: 'AED', country: 'United Arab Emirates', countryAr: 'الإمارات العربية المتحدة', rate: 9.74, direction: 'ltr' },
      'ar-ae': { code: 'AED', symbol: 'د.إ', country: 'United Arab Emirates', countryAr: 'الإمارات العربية المتحدة', rate: 9.74, direction: 'rtl' },
      'en-kw': { code: 'KWD', symbol: 'KWD', country: 'Kuwait', countryAr: 'الكويت', rate: 0.82, direction: 'ltr' },
      'ar-kw': { code: 'KWD', symbol: 'د.ك', country: 'Kuwait', countryAr: 'الكويت', rate: 0.82, direction: 'rtl' },
      'en-om': { code: 'OMR', symbol: 'OMR', country: 'Oman', countryAr: 'عمان', rate: 1.02, direction: 'ltr' },
      'ar-om': { code: 'OMR', symbol: 'ر.ع.', country: 'Oman', countryAr: 'عمان', rate: 1.02, direction: 'rtl' },
      'en-qa': { code: 'QAR', symbol: 'QAR', country: 'Qatar', countryAr: 'قطر', rate: 9.66, direction: 'ltr' },
      'ar-qa': { code: 'QAR', symbol: 'ر.ق', country: 'Qatar', countryAr: 'قطر', rate: 9.66, direction: 'rtl' }
    };

    // Base currency for all prices (assumed USD in Shopify)
    this.baseCurrency = 'USD';
    this.baseSymbol = '$';
    this.baseRate = 3.641; // 1 USD = 3.641 BHD (reference rate)

    // Initialize converter
    this.init();
  }

  init() {
    // Get current locale from URL
    this.currentLocale = this.getLocaleFromURL();



    
    // Store current locale
    this.setStoredLocale(this.currentLocale);

    // Only initialize converter if NOT in Bahrain
    // Bahrain is the store's base currency, so no conversion needed
    if (!this.currentLocale.endsWith('bh')) {


      
      // Multiple sweeps to catch all prices, including lazy-loaded and animated content
      // Homepage uses AOS (Animate On Scroll) which may delay element rendering
      setTimeout(() => {

        this.convertPrices();
      }, 500);
      
      setTimeout(() => {

        this.convertPrices();
      }, 1500);
      
      setTimeout(() => {

        this.convertPrices();
      }, 2500);
      
      this.observeDOM();
    } else {

    }

    // Update currency info in page (always do this)
    this.updateCurrencyInfo();

    // Listen for page changes (AJAX)
    this.setupCartObserver();

    // Override theme.Currency.formatMoney to apply currency conversion
    this.overrideThemeCurrencyFormatter();

    // Log initialization



  }

  /**
   * Override theme.Currency.formatMoney to convert prices
   */
  overrideThemeCurrencyFormatter() {
    const self = this;
    
    // Only override for non-Bahrain markets
    if (this.currentLocale.endsWith('bh')) {

      return;
    }

    
    // Wait for theme to be available
    const checkForTheme = setInterval(() => {
      if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
        clearInterval(checkForTheme);
        
        const originalFormatMoney = window.theme.Currency.formatMoney;
        
        window.theme.Currency.formatMoney = function(cents, format) {
          // Call original formatter to get BHD formatted string
          const bhdFormatted = originalFormatMoney.call(this, cents, format);
          
          // Extract the number from the formatted string
          const numberMatch = bhdFormatted.match(/[\d,\.]+/);
          if (!numberMatch) {
            return bhdFormatted; // Return as-is if can't parse
          }
          
          const bhdAmount = parseFloat(numberMatch[0].replace(/,/g, '.'));
          
          // Convert from BHD to target currency
          const convertedAmount = self.convertPrice(bhdAmount);
          
          // Format with target currency
          const config = self.getCurrencyConfig();
          const decimals = (config.code === 'KWD' || config.code === 'OMR') ? 3 : 2;
          const formattedAmount = convertedAmount.toFixed(decimals);
          
          // Build formatted string with target currency
          let result;
          if (config.direction === 'rtl') {
            result = `${formattedAmount} ${config.symbol}`;
          } else {
            result = `${config.symbol} ${formattedAmount}`;
          }

          return result;
        };

      }
    }, 100);
    
    // Also override Shopify.formatMoney if available
    const checkForShopify = setInterval(() => {
      if (window.Shopify && window.Shopify.formatMoney) {
        clearInterval(checkForShopify);
        
        const originalShopifyFormat = window.Shopify.formatMoney;
        
        window.Shopify.formatMoney = function(cents, format) {
          // Call original formatter
          const bhdFormatted = originalShopifyFormat.call(this, cents, format);
          
          // Extract the number
          const numberMatch = bhdFormatted.match(/[\d,\.]+/);
          if (!numberMatch) {
            return bhdFormatted;
          }
          
          const bhdAmount = parseFloat(numberMatch[0].replace(/,/g, '.'));
          
          // Convert from BHD to target currency
          const convertedAmount = self.convertPrice(bhdAmount);
          
          // Format with target currency
          const config = self.getCurrencyConfig();
          const decimals = (config.code === 'KWD' || config.code === 'OMR') ? 3 : 2;
          const formattedAmount = convertedAmount.toFixed(decimals);
          
          // Build formatted string with target currency
          let result;
          if (config.direction === 'rtl') {
            result = `${formattedAmount} ${config.symbol}`;
          } else {
            result = `${config.symbol} ${formattedAmount}`;
          }

          return result;
        };

      }
    }, 100);
    
    // Timeout after 5 seconds
    setTimeout(() => {
      clearInterval(checkForTheme);
      clearInterval(checkForShopify);
    }, 5000);
  }

  /**
   * Extract locale from current URL
   */
  getLocaleFromURL() {
    const pathname = window.location.pathname;
    // Match pattern like /en-bh/, /ar-sa/, /en-ae, etc. (with or without trailing slash)
    const match = pathname.match(/\/(en|ar)-(bh|sa|ae|kw|om|qa)(?:\/|$)/);
    
    if (match) {
      return `${match[1]}-${match[2]}`;
    }

    // Fallback to default locale
    return 'en-bh';
  }

  /**
   * Get currency config for current locale
   */
  getCurrencyConfig(locale = this.currentLocale) {
    return this.currencies[locale] || this.currencies['en-bh'];
  }

  /**
   * Convert price from BHD (store currency) to target currency
   */
  convertPrice(priceInBHD) {
    // Input is in BHD (store's base currency)
    // We need to convert it to the target currency
    const config = this.getCurrencyConfig();
    
    // Since all prices on the page are in BHD, multiply by the exchange rate
    // which is the target currency value relative to BHD
    const convertedPrice = parseFloat(priceInBHD) * config.rate;
    return parseFloat(convertedPrice.toFixed(3)); // Round to 3 decimals
  }

  /**
   * Format price with currency symbol
   */
  formatPrice(price, locale = this.currentLocale) {
    const config = this.getCurrencyConfig(locale);
    const isArabic = locale.startsWith('ar');
    
    // Determine decimal places based on currency
    const decimals = (config.code === 'KWD' || config.code === 'OMR') ? 3 : 2;
    const formattedPrice = price.toFixed(decimals);
    
    if (isArabic) {
      return `${formattedPrice} ${config.symbol}`;
    } else {
      return `${config.symbol} ${formattedPrice}`;
    }
  }

  /**
   * Convert all prices on the page
   */
  convertPrices() {
    // Don't convert for Bahrain (base currency)
    if (this.currentLocale.endsWith('bh')) {

      return;
    }





    
    // Strategy 1: Target price display divs with class product-card__price
    const priceContainers = document.querySelectorAll('.product-card__price:not([data-converted])');

    
    priceContainers.forEach((container, index) => {

      
      // Find the parent card to get the data-price value
      const card = container.closest('[data-price]');
      if (!card) {

        return;
      }
      
      const dataPriceAttr = card.getAttribute('data-price');
      const bhdPrice = parseFloat(dataPriceAttr) / 100;
      
      if (bhdPrice > 0) {
        const convertedPrice = this.convertPrice(bhdPrice);
        const formattedPrice = this.formatPrice(convertedPrice);

        
        // Replace ALL text nodes in the price container
        this.replaceTextInNode(container, bhdPrice, formattedPrice);
        
        container.setAttribute('data-converted', 'true');
      }
    });

    // Strategy 2: Fallback to finding elements with data-price if no price containers found
    if (priceContainers.length === 0) {

      const productCards = document.querySelectorAll('[data-price]:not([data-converted-price])');

      
      productCards.forEach((card, index) => {
        const dataPriceAttr = card.getAttribute('data-price');
        
        if (dataPriceAttr) {
          const bhdPrice = parseFloat(dataPriceAttr) / 100;
          
          if (bhdPrice > 0) {
            const convertedPrice = this.convertPrice(bhdPrice);
            const formattedPrice = this.formatPrice(convertedPrice);

            
            this.replaceTextInNode(card, bhdPrice, formattedPrice);
            card.setAttribute('data-converted-price', convertedPrice);
          }
        }
      });
    }

    // Strategy 3: Universal price targeting - find all elements with price-like content
    this.convertUniversalPrices();

    // Strategy 4: Specific product page price conversion
    this.convertProductPagePrices();

    // Convert cart prices
    this.convertCartPrices();

    // Convert collection page prices  
    this.convertCollectionPrices();
  }

  /**
   * Universal price conversion - finds all price-like content on the page
   */
  convertUniversalPrices() {
    // Skip for Bahrain (base currency)
    if (this.currentLocale.endsWith('bh')) {
      return;
    }

    // Selectors for different price types across pages
    const priceSelectors = [
      '.product-single__price',  // Product page prices (Perfumista theme)
      '.price-item',             // Product card prices (Perfumista theme)
      '.product-card__price',    // Homepage product card prices
      '.js-cart-total',          // Mini cart total
      '.mini-cart-item span',    // Mini cart item prices
      '.bottom-total span',      // Cart drawer total
      '.price',
      '[data-price]',
      '.product-price',
      '.product__price',
      '.money',
      '[data-money]',
      '.product-form__price',
      '.cart-item__price',
      '.line-item-total',
      '.line-item-price',
      '.order-summary__subtotal',
      '.order-summary__total',
      '[data-line-item-price]',
      '[data-cart-total]',
    ];

    let totalConversions = 0;

    priceSelectors.forEach(selector => {
      const elements = document.querySelectorAll(`${selector}:not([data-currency-converted])`);
      
      if (elements.length > 0) {

        
        elements.forEach(el => {
          // Skip if already converted
          if (el.getAttribute('data-currency-converted')) {
            return;
          }

          const text = el.textContent.trim();
          
          // Check if this looks like a price in multiple formats:
          // 1. "BHD 100" or "100 BHD" or "AED 100" or "100 AED" or "Ø¯.Ø¥ 100"
          // 2. Just a number like "100" or "100.50" or "100,50"
          // 3. Currency symbol + number like "$100" or "100 $"
          let priceMatch = text.match(/(?:BHD|AED|Ø¯\.Ø¥)\s*([\d,\.]+)|([\d,\.]+)\s*(?:BHD|AED|Ø¯\.Ø¥)|^\s*([\d,\.]+)\s*$/);
          
          if (!priceMatch && /^\s*[\d,\.]+\s*$/.test(text)) {
            // It's just a number - treat as price
            priceMatch = text.match(/([\d,\.]+)/);
          }
          
          if (priceMatch) {
            // Extract the price value from any match group
            let priceStr = priceMatch[1] || priceMatch[2] || priceMatch[3] || priceMatch[0];
            const bhdPrice = parseFloat(priceStr.replace(/,/g, '.'));
            
            // Only convert if it looks like a reasonable price (between 0.1 and 1000000)
            if (bhdPrice > 0.1 && bhdPrice < 1000000) {
              const convertedPrice = this.convertPrice(bhdPrice);
              const formattedPrice = this.formatPrice(convertedPrice);

              
              // Only replace if this is a leaf element (no children) or only has text
              if (el.children.length === 0 || (el.textContent && !el.innerHTML.includes('<'))) {
                el.textContent = formattedPrice;
              } else {
                // If has children, try to replace text nodes
                this.replaceTextInNode(el, bhdPrice, formattedPrice);
              }
              
              el.setAttribute('data-currency-converted', 'true');
              totalConversions++;
            }
          }
        });
      }
    });

    // Special handler for .product-card__price (homepage products)

    const productCardPrices = document.querySelectorAll('.product-card__price:not([data-currency-converted])');

    
    productCardPrices.forEach((priceDiv, index) => {
      // Get all text nodes from the price div
      const textContent = priceDiv.textContent.trim();

      
      // Look for BHD price pattern - match "BHD XXX.XXX" or "BHD XXX,XXX"
      const bhdMatch = textContent.match(/BHD\s*([\d,\.]+)/i);
      if (bhdMatch) {
        const bhdPriceStr = bhdMatch[1];
        const bhdPrice = parseFloat(bhdPriceStr.replace(/,/g, '.'));

        
        if (bhdPrice > 0.1 && bhdPrice < 1000000) {
          const convertedPrice = this.convertPrice(bhdPrice);
          const formattedPrice = this.formatPrice(convertedPrice);

          
          // Replace the text in the div while preserving structure
          this.replaceTextInNode(priceDiv, `BHD ${bhdPriceStr}`, formattedPrice);
          
          priceDiv.setAttribute('data-currency-converted', 'true');
          totalConversions++;
        }
      } else {

      }
    });

  }

  /**
   * Convert prices specifically on product pages
   * Handles the .product-single__price element and related price spans
   */
  convertProductPagePrices() {
    // Skip for Bahrain (base currency)
    if (this.currentLocale.endsWith('bh')) {
      return;
    }


    // Find all product single price elements
    const productPrices = document.querySelectorAll('.product-single__price:not([data-currency-converted])');


    productPrices.forEach((priceEl, index) => {
      // Check if element has a data-price attribute (in cents)
      const dataPrice = priceEl.getAttribute('data-price');
      if (dataPrice) {
        const bhdPrice = parseFloat(dataPrice) / 100;
        
        if (bhdPrice > 0.1 && bhdPrice < 1000000) {
          const convertedPrice = this.convertPrice(bhdPrice);
          const formattedPrice = this.formatPrice(convertedPrice);

          
          // Get the text content and replace the price
          const textContent = priceEl.textContent.trim();
          
          // Find the price number in the text (handles formats like "100.00" or "100,00")
          const numberMatch = textContent.match(/([\d,\.]+)/);
          if (numberMatch) {
            const oldPriceStr = numberMatch[0];
            const newText = textContent.replace(oldPriceStr, formattedPrice);
            priceEl.textContent = newText;
          } else {
            // If no number found, just replace the whole thing
            priceEl.textContent = formattedPrice;
          }
          
          priceEl.setAttribute('data-currency-converted', 'true');
        }
      } else {
        // No data-price attribute, try to extract from text content
        const textContent = priceEl.textContent.trim();

        
        // Look for currency prefix patterns or plain numbers
        let bhdPrice = null;
        let originalPriceStr = null;
        
        // Pattern 1: "BHD 100" or "BHD100"
        const bhdPattern = textContent.match(/BHD\s*([\d,\.]+)/i);
        if (bhdPattern) {
          originalPriceStr = bhdPattern[0];
          bhdPrice = parseFloat(bhdPattern[1].replace(/,/g, '.'));
        }
        
        // Pattern 2: "100 BHD" or "100BHD"
        if (!bhdPrice) {
          const bhdPattern2 = textContent.match(/([\d,\.]+)\s*BHD/i);
          if (bhdPattern2) {
            originalPriceStr = bhdPattern2[0];
            bhdPrice = parseFloat(bhdPattern2[1].replace(/,/g, '.'));
          }
        }
        
        // Pattern 3: Just a number (assume it's in BHD)
        if (!bhdPrice) {
          const numPattern = textContent.match(/^([\d,\.]+)$/);
          if (numPattern) {
            originalPriceStr = numPattern[0];
            bhdPrice = parseFloat(numPattern[1].replace(/,/g, '.'));
          }
        }
        
        if (bhdPrice && bhdPrice > 0.1 && bhdPrice < 1000000) {
          const convertedPrice = this.convertPrice(bhdPrice);
          const formattedPrice = this.formatPrice(convertedPrice);

          
          // Replace in the text
          const newText = textContent.replace(originalPriceStr || bhdPrice.toString(), formattedPrice);
          priceEl.textContent = newText;
          priceEl.setAttribute('data-currency-converted', 'true');
        }
      }
    });

    // Also handle compare prices
    const comparePrices = document.querySelectorAll('.product-single__price--compare:not([data-currency-converted])');

    
    comparePrices.forEach((priceEl, index) => {
      const textContent = priceEl.textContent.trim();
      const numMatch = textContent.match(/([\d,\.]+)/);
      
      if (numMatch) {
        const bhdPrice = parseFloat(numMatch[1].replace(/,/g, '.'));
        
        if (bhdPrice > 0.1 && bhdPrice < 1000000) {
          const convertedPrice = this.convertPrice(bhdPrice);
          const formattedPrice = this.formatPrice(convertedPrice);

          
          const newText = textContent.replace(numMatch[1], formattedPrice);
          priceEl.textContent = newText;
          priceEl.setAttribute('data-currency-converted', 'true');
        }
      }
    });
  }

  /**
   * Replace text nodes matching a price with new formatted price

   */
  replaceTextInNode(node, originalPrice, newPrice) {
    const walker = document.createTreeWalker(
      node,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
    
    let textNode;
    const nodesToReplace = [];
    
    // Collect all text nodes that look like prices
    while (textNode = walker.nextNode()) {
      const text = textNode.textContent.trim();
      
      // Check if this text contains currency or numbers that look like prices
      if (text.includes('BHD') || /[\d,\.]+/.test(text)) {
        nodesToReplace.push({
          node: textNode,
          originalText: textNode.textContent,
          text: text
        });
      }
    }

    
    // Replace the text nodes
    nodesToReplace.forEach((item, i) => {
      let newText = item.originalText;
      
      // Try different replacement strategies in order
      
      // Strategy 1: Replace "BHD ###" pattern
      if (/BHD\s+[\d,\.]+/i.test(item.originalText)) {
        newText = item.originalText.replace(/BHD\s+[\d,\.]+/i, newPrice);

      }
      
      // Strategy 2: Replace "### BHD" pattern
      else if (/[\d,\.]+\s+BHD/i.test(item.originalText)) {
        newText = item.originalText.replace(/[\d,\.]+\s+BHD/i, newPrice);

      }
      
      // Strategy 3: If text is JUST a price number, replace it entirely
      else if (/^[\d,\.]+$/.test(item.text)) {
        newText = newPrice;

      }
      
      // Only update if something changed
      if (newText !== item.originalText) {
        item.node.textContent = newText;
      }
    });
  }

  /**
   * Extract USD price from text - improved to handle Shopify money formats
   */
  extractUSDPrice(priceText) {
    // Remove currency symbols and extra whitespace
    let cleaned = priceText.trim();
    
    // Remove common currency symbols: $, Ø¯.Ø¨, Ø±.Ø³, Ø¯.Ø¥, Ø¯.Ùƒ, Ø±.Ø¹., Ø±.Ù‚, etc
    cleaned = cleaned.replace(/[\$\d.Ø¨\s,Ø±\-Ø¥ÙƒØ¹Ù‚\.]/g, '');
    
    // Extract first number sequence (digits with optional decimal point)
    const match = cleaned.match(/(\d+\.?\d*)/);
    return match ? parseFloat(match[1]) : null;
  }

  /**
   * Convert cart prices
   */
  convertCartPrices() {
    // Don't convert for Bahrain
    if (this.currentLocale.endsWith('bh')) {
      return;
    }

    
    // Cart totals in mini cart drawer
    const cartTotal = document.querySelector('.js-cart-total');
    if (cartTotal && !cartTotal.getAttribute('data-currency-converted')) {
      const text = cartTotal.textContent.trim();
      
      // Extract price - could be "BHD ###.##" or "AED ###.##" or just a number
      let priceMatch = text.match(/BHD\s*([\d,\.]+)|AED\s*([\d,\.]+)|Ø¯\.Ø¥\s*([\d,\.]+)|([\d,\.]+)/);
      
      if (priceMatch) {
        let priceStr = priceMatch[1] || priceMatch[2] || priceMatch[3] || priceMatch[4] || '0';
        const bhdPrice = parseFloat(priceStr.replace(/,/g, '.'));
        
        if (bhdPrice > 0) {
          const convertedPrice = this.convertPrice(bhdPrice);
          const formattedPrice = this.formatPrice(convertedPrice);

          cartTotal.textContent = formattedPrice;
          cartTotal.setAttribute('data-currency-converted', 'true');
        }
      }
    }

    // Mini-cart items (in drawer)
    const miniCartItems = document.querySelectorAll('.mini-cart-item');
    miniCartItems.forEach((item, index) => {
      if (!item.getAttribute('data-currency-converted')) {
        // Find price span in this item
        const priceSpan = item.querySelector('span');
        if (priceSpan) {
          const text = priceSpan.textContent.trim();
          let priceMatch = text.match(/BHD\s*([\d,\.]+)|AED\s*([\d,\.]+)|Ø¯\.Ø¥\s*([\d,\.]+)|([\d,\.]+)/);
          
          if (priceMatch) {
            let priceStr = priceMatch[1] || priceMatch[2] || priceMatch[3] || priceMatch[4] || '0';
            const bhdPrice = parseFloat(priceStr.replace(/,/g, '.'));
            
            if (bhdPrice > 0) {
              const convertedPrice = this.convertPrice(bhdPrice);
              const formattedPrice = this.formatPrice(convertedPrice);

              priceSpan.textContent = formattedPrice;
              item.setAttribute('data-currency-converted', 'true');
            }
          }
        }
      }
    });

    // Line item prices (in full cart page)
    const lineItems = document.querySelectorAll('[data-line-item-price], .line-item-price');
    lineItems.forEach((item, index) => {
      if (!item.getAttribute('data-currency-converted')) {
        const text = item.textContent.trim();
        let priceMatch = text.match(/BHD\s*([\d,\.]+)|AED\s*([\d,\.]+)|Ø¯\.Ø¥\s*([\d,\.]+)|([\d,\.]+)/);
        
        if (priceMatch) {
          let priceStr = priceMatch[1] || priceMatch[2] || priceMatch[3] || priceMatch[4] || '0';
          const bhdPrice = parseFloat(priceStr.replace(/,/g, '.'));
          
          if (bhdPrice > 0 && bhdPrice < 1000000) {
            const convertedPrice = this.convertPrice(bhdPrice);
            const formattedPrice = this.formatPrice(convertedPrice);

            item.textContent = formattedPrice;
            item.setAttribute('data-currency-converted', 'true');
          }
        }
      }
    });
  }

  /**
   * Convert collection page prices
   */
  convertCollectionPrices() {
    // Don't convert for Bahrain
    if (this.currentLocale.endsWith('bh')) {
      return;
    }

    // This is now handled by convertPrices() which processes all [data-price] elements
    // This method is kept for backwards compatibility

  }

  /**
   * Observe DOM for dynamic content changes
   */
  observeDOM() {
    // Don't observe for Bahrain (base currency)
    if (this.currentLocale.endsWith('bh')) {

      return;
    }


    const observer = new MutationObserver((mutations) => {
      let shouldConvert = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          // Check if new price elements were added
          if (mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1) { // Element node
                if (node.classList && (
                  node.classList.contains('price') ||
                  node.classList.contains('price-item') ||
                  node.classList.contains('product-price') ||
                  node.classList.contains('product-single__price') ||
                  node.classList.contains('money') ||
                  node.classList.contains('product__price') ||
                  node.classList.contains('product-form__price') ||
                  node.classList.contains('line-item-price') ||
                  node.classList.contains('card-wrapper') ||
                  node.classList.contains('product-card__price') ||
                  node.getAttribute('data-price') ||
                  node.getAttribute('data-money') ||
                  node.getAttribute('data-line-item-price') ||
                  node.getAttribute('data-cart-total')
                )) {
                  shouldConvert = true;
                }
                // Also check descendants
                if (node.querySelector && node.querySelector(
                  '[data-price], [data-money], .price, .price-item, .product-card__price, .product-single__price, .product-price, .money, .product__price, .product-form__price, .line-item-price, [data-line-item-price], [data-cart-total]'
                )) {
                  shouldConvert = true;
                }
              }
            });
          }
        }
      });

      if (shouldConvert) {
        // Debounce the conversion
        clearTimeout(this.convertTimeout);
        this.convertTimeout = setTimeout(() => {

          this.convertPrices();
        }, 100);
      }
    });

    // Start observing the document
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  /**
   * Store locale in localStorage
   */
  setStoredLocale(locale) {
    try {
      localStorage.setItem('perfumista_locale', locale);
    } catch (e) {

    }
  }

  /**
   * Get stored locale
   */
  getStoredLocale() {
    try {
      return localStorage.getItem('perfumista_locale') || this.currentLocale;
    } catch (e) {
      return this.currentLocale;
    }
  }

  /**
   * Get all available currencies
   */
  getAvailableCurrencies() {
    return this.currencies;
  }

  /**
   * Get current currency info
   */
  getCurrentCurrency() {
    return {
      locale: this.currentLocale,
      ...this.getCurrencyConfig()
    };
  }

  /**
   * Update currency info in DOM
   */
  updateCurrencyInfo() {
    const currency = this.getCurrentCurrency();
    const currencyInfo = document.querySelector('[data-currency-info]');
    
    if (currencyInfo) {
      currencyInfo.setAttribute('data-currency-code', currency.code);
      currencyInfo.setAttribute('data-currency-symbol', currency.symbol);
      currencyInfo.setAttribute('data-currency-locale', currency.locale);
    }

    // Update data attributes on body for CSS hooks
    document.documentElement.setAttribute('data-currency', currency.code);
    document.documentElement.setAttribute('data-locale', currency.locale);
    
    // Update direction and language for the entire page
    const config = this.getCurrencyConfig();
    const direction = config.direction || (currency.locale.startsWith('ar') ? 'rtl' : 'ltr');
    const language = currency.locale.split('-')[0]; // en or ar
    
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', language);
    document.body.setAttribute('dir', direction);

    
    // Dispatch custom event for RTL/language change
    const rtlChangeEvent = new CustomEvent('perfumista:rtl-change', {
      detail: {
        isRTL: direction === 'rtl',
        locale: currency.locale,
        direction: direction,
        language: language
      },
      bubbles: true
    });
    document.dispatchEvent(rtlChangeEvent);

  }

  /**
   * Setup observer for Shopify cart updates
   */
  setupCartObserver() {
    const self = this;
    
    // Listen for cart updates
    document.addEventListener('cart:updated', (e) => {
      if (e.detail && e.detail.cart) {
        setTimeout(() => {
          self.convertCartPrices();
          self.convertPrices();
        }, 100);
      }
    });

    // Watch for theme.miniCart updates
    if (window.theme && window.theme.miniCart) {
      const originalUpdateElements = window.theme.miniCart.updateElements;
      const originalGenerateCart = window.theme.miniCart.generateCart;
      
      if (typeof originalUpdateElements === 'function') {
        window.theme.miniCart.updateElements = function() {
          originalUpdateElements.apply(this, arguments);
          setTimeout(() => {
            self.convertCartPrices();
            self.convertPrices();
          }, 100);
        };
      }
      
      if (typeof originalGenerateCart === 'function') {
        window.theme.miniCart.generateCart = function() {
          originalGenerateCart.apply(this, arguments);
          setTimeout(() => {
            self.convertCartPrices();
            self.convertPrices();
          }, 100);
        };
      }
    }

    // Also watch for Shopify AJAX API cart changes
    if (window.Shopify && window.Shopify.cart) {
      const originalFetch = window.Shopify.cart.fetch;
      
      if (typeof originalFetch === 'function') {
        window.Shopify.cart.fetch = function() {
          const result = originalFetch.apply(this, arguments);
          if (result && typeof result.then === 'function') {
            result.then(() => {
              setTimeout(() => {
                self.convertCartPrices();
                self.convertPrices();
              }, 100);
            });
          }
          return result;
        };
      }
    }
  }

  /**
   * Get locale parts (language and country)
   */
  getLocaleParts(locale = this.currentLocale) {
    const parts = locale.split('-');
    return {
      language: parts[0],
      country: parts[1]
    };
  }

  /**
   * Check if current locale is Arabic
   */
  isArabic(locale = this.currentLocale) {
    return locale.startsWith('ar');
  }

  /**
   * Get market info
   */
  getMarketInfo(locale = this.currentLocale) {
    const config = this.getCurrencyConfig(locale);
    const parts = this.getLocaleParts(locale);
    
    return {
      locale,
      language: parts.language,
      country: parts.country,
      countryName: parts.language === 'ar' ? config.countryAr : config.country,
      currency: config.code,
      symbol: config.symbol,
      rate: config.rate,
      direction: config.direction
    };
  }

  /**
   * Manually set locale and refresh prices
   */
  setLocale(locale) {
    if (this.currencies[locale]) {
      this.currentLocale = locale;
      this.setStoredLocale(locale);
      
      // Navigate to locale-specific market URL to actually change the market
      this.switchMarket(locale);
      
      return true;
    }
    return false;
  }

  /**
   * Switch to a different market/locale by navigating to the appropriate URL
   */
  switchMarket(locale) {
    // Get current path
    let currentPath = window.location.pathname;
    let currentSearch = window.location.search;
    
    // Remove existing locale prefix from path
    // Match patterns like /en-bh/, /ar-sa/, /en/, /ar/
    currentPath = currentPath.replace(/^\/(en|ar)(?:-(bh|sa|ae|kw|om|qa))?\//, '/');
    
    // Ensure path starts with /
    if (!currentPath.startsWith('/')) {
      currentPath = '/' + currentPath;
    }
    
    // Build new URL with locale prefix
    const newPath = `/${locale}${currentPath}`;
    const newUrl = newPath + currentSearch;


    
    // Navigate to new market URL
    window.location.href = newUrl;
  }

  /**
   * Reset to default currency (Bahrain BHD - no conversion)
   */
  resetToDefault() {
    this.currentLocale = 'en-bh';
    this.setStoredLocale('en-bh');
    
    // Navigate to Bahrain market
    this.switchMarket('en-bh');
    
    return true;
  }

  /**
   * Reset all prices to original (no conversion)
   */
  resetPrices() {
    // Reset product prices
    const priceElements = document.querySelectorAll('[data-price], .price, .product-price, [data-productprice]');
    
    priceElements.forEach(el => {
      const originalPrice = el.getAttribute('data-original-price');
      const dataPriceAttr = el.getAttribute('data-price');
      
      let priceToUse = null;
      if (originalPrice) {
        priceToUse = parseFloat(originalPrice);
      } else if (dataPriceAttr) {
        priceToUse = parseFloat(dataPriceAttr);
      }
      
      if (priceToUse) {
        // Format without conversion (BHD)
        const formatted = this.formatPrice(priceToUse, 'en-bh');
        el.textContent = formatted;
        el.removeAttribute('data-converted-price');
        if (!el.getAttribute('data-original-price')) {
          el.setAttribute('data-original-price', priceToUse);
        }
      }
    });

    // Reset cart prices
    this.resetCartPrices();

    // Reset collection prices
    this.resetCollectionPrices();
  }

  /**
   * Reset cart prices to original
   */
  resetCartPrices() {
    const cartTotal = document.querySelector('[data-cart-total], .cart-total');
    if (cartTotal) {
      const originalPrice = cartTotal.getAttribute('data-original-price');
      if (originalPrice) {
        const formatted = this.formatPrice(parseFloat(originalPrice), 'en-bh');
        cartTotal.textContent = formatted;
      }
    }

    const lineItems = document.querySelectorAll('[data-line-item-price], .line-item-price');
    lineItems.forEach(item => {
      const originalPrice = item.getAttribute('data-original-price');
      const dataPriceAttr = item.getAttribute('data-price');
      
      let priceToUse = null;
      if (originalPrice) {
        priceToUse = parseFloat(originalPrice);
      } else if (dataPriceAttr) {
        priceToUse = parseFloat(dataPriceAttr);
      }
      
      if (priceToUse) {
        const formatted = this.formatPrice(priceToUse, 'en-bh');
        item.textContent = formatted;
        if (!item.getAttribute('data-original-price')) {
          item.setAttribute('data-original-price', priceToUse);
        }
      }
    });
  }

  /**
   * Reset collection prices to original
   */
  resetCollectionPrices() {
    const productCards = document.querySelectorAll('[data-product-card], .product-card, .product-item');
    
    productCards.forEach(card => {
      const priceEl = card.querySelector('[data-price], .price, .product-price');
      if (priceEl) {
        const originalPrice = priceEl.getAttribute('data-original-price');
        const dataPriceAttr = priceEl.getAttribute('data-price');
        
        let priceToUse = null;
        if (originalPrice) {
          priceToUse = parseFloat(originalPrice);
        } else if (dataPriceAttr) {
          priceToUse = parseFloat(dataPriceAttr);
        }
        
        if (priceToUse) {
          const formatted = this.formatPrice(priceToUse, 'en-bh');
          priceEl.textContent = formatted;
          if (!priceEl.getAttribute('data-original-price')) {
            priceEl.setAttribute('data-original-price', priceToUse);
          }
        }
      }
    });
  }

  /**
   * Get all market locales
   */
  getMarkets() {
    const markets = {};
    Object.keys(this.currencies).forEach(locale => {
      const parts = this.getLocaleParts(locale);
      const country = parts.country.toUpperCase();
      
      if (!markets[country]) {
        markets[country] = [];
      }
      markets[country].push(locale);
    });
    return markets;
  }
}

// Initialize currency converter when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.currencyConverter = new CurrencyConverter();
  });
} else {
  window.currencyConverter = new CurrencyConverter();
}

// Expose globally for debugging
if (typeof window !== 'undefined') {
  window.CurrencyConverter = CurrencyConverter;
}
